import { ApiHeaderInfoOk } from 'src/types/v1-api-types';
import { ErrorBoundary, LinkStyled } from '@components/common';
import { LogoBjFull } from '@components/layout/LogoBj/LogoBjFull';
import { LogoBj } from './LogoBj';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { getI18nLinkHref } from '@services/i18nPaths';

interface LogoBjInterface {
  headerInfo?: ApiHeaderInfoOk;
  locale?: Locale;
  className?: string;
  iconColor?: string;
  textColor?: string;
  width?: string;
  height?: string;
  imageClassName?: string;
  showFullLogo?: boolean;
}

/**
 * @description Bestjobs logo component. This component has links to the jobs pages, depending on
 * the user role. If the user is not authenticated, the link will be to the home page.
 */
export const LogoBjLink = (props: LogoBjInterface) => {
  const {
    headerInfo,
    locale : propLocale,
    className = '',
    iconColor = 'fill-secondary',
    textColor = 'fill-ink',
    width = '100%',
    height = '100%',
    imageClassName = '',
    showFullLogo = false,
  } = props;


  // Resolve the active locale and the localized home URL
  const { locale: routerLocale } = useExtendedRouter();
  const usedLocale = (propLocale || routerLocale) as Locale;
  const homeHref = getI18nLinkHref('/', usedLocale);


  const isCompany = !headerInfo || (headerInfo && headerInfo.role === 'COMPANY');
  const isUser = !headerInfo || (headerInfo && headerInfo.role === 'USER');

  // If the user is authenticated, we need to redirect him to the correct homepage page
  // *******************************************************************************
  if (headerInfo) {
    return (
      <ErrorBoundary
        fallback={<div className="py-1.5 text-xl font-bold">Bestjobs</div>}
      >
        <LinkStyled
          href="/"
          locale={usedLocale}
          aria-label="bestjobs"
          className={className}
          styling="none"
          onClick={(e) => {
            e.preventDefault();
            window.location.href = homeHref;
          }}
        >
          <LogoBj />
        </LinkStyled>
      </ErrorBoundary>
    );
  }

  // If the user is not authenticated, we set the link to the home page or live page.
  // *******************************************************************************
  return (
    <ErrorBoundary
      fallback={<div className="py-1.5 text-xl font-bold">Bestjobs</div>}
    >
      <LinkStyled
        href="/"
        locale={usedLocale}
        aria-label="bestjobs"
        className={className}
        styling="none"
        onClick={(e) => {
          e.preventDefault();
          window.location.href = homeHref;
        }}
      >
        {(isCompany || isUser) && !showFullLogo ? (
          <>
            <LogoBj />
          </>
        ) : (
          <LogoBjFull
            iconColor={iconColor}
            textColor={textColor}
            width={width}
            height={height}
            className={imageClassName}
          />
        )}
      </LinkStyled>
    </ErrorBoundary>
  );
};
